import { Sheet } from '../../../utils/download-utils';
import { DataQaDataset, MmmConfig, WeekRow } from './data-qa-types';
import { filterWeeksToFencepost } from './data-qa-utils';
import { ModelStage, ModelWithStages } from './hooks/useAllModelStages';
import { exportSheetName, modelSheetParts } from './model-export-utils';

interface DependentVarColumn {
  label: string;
  value: (week: WeekRow) => number;
}

// Mirrors DependentVarSection's own rendering (DepTargetCard / BrandIndexCard) so the
// export can never disagree with the chart: Brand Equity's BrandIndex stage reads each
// metric off perBrandHealthVar, every other stage reads dependentValue.
const stageColumns = (config: MmmConfig, dataset: DataQaDataset): DependentVarColumn[] =>
  config.brandHealthVars
    ? config.brandHealthVars.map(metric => ({
        label: metric.label,
        value: (week: WeekRow) => week.perBrandHealthVar?.[metric.key] ?? 0
      }))
    : dataset.dependentVarStats.map(stat => ({
        label: stat.label,
        value: (week: WeekRow) => week.perBrandHealthVar?.[stat.key] ?? week.dependentValue
      }));

const stageNumber = (config: MmmConfig, index: number): number => {
  const match = /^s(\d+)$/.exec(config.brandEquityStage ?? '');
  return match ? Number(match[1]) : index + 1;
};

// One row per date within each stage's own fencepost window (the same weeks the
// Dependent Variable chart shows), Date plus each stage's dependent variable(s). A
// multi-stage model (Brand Equity) gets "Stage N: " column prefixes and its stages are
// outer-joined on date -- each stage keeps its own fencepost, so a date only one
// stage covers leaves the other stage's cells blank rather than dropping the row.
export const buildDependentVarExportRows = (
  stages: ModelStage[]
): Record<string, string | number>[] => {
  const multiStage = stages.length > 1;
  const valuesByDate = new Map<string, Record<string, number>>();
  const allLabels: string[] = [];

  stages.forEach(({ config, dataset }, index) => {
    const prefix = multiStage ? `Stage ${stageNumber(config, index)}: ` : '';
    const columns = stageColumns(config, dataset).map(col => ({
      ...col,
      label: `${prefix}${col.label}`
    }));
    allLabels.push(...columns.map(col => col.label));
    const weeks = filterWeeksToFencepost(
      dataset.weeks,
      config.fencepostStart,
      config.fencepostEnd,
      dataset.reviewGranularity
    );
    weeks.forEach(week => {
      const values = valuesByDate.get(week.weekIso) ?? {};
      columns.forEach(col => {
        values[col.label] = col.value(week);
      });
      valuesByDate.set(week.weekIso, values);
    });
  });

  return [...valuesByDate.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map(date => {
      const values = valuesByDate.get(date) ?? {};
      const row: Record<string, string | number> = { Date: date };
      allLabels.forEach(label => {
        row[label] = values[label] ?? '';
      });
      return row;
    });
};

// One sheet per model, "<company>_dv_export_<model>".
export const buildDependentVarExportSheets = (
  company: string,
  models: ModelWithStages[]
): Sheet[] => {
  const parts = modelSheetParts(models);
  return models.map((model, i) => ({
    name: exportSheetName(company, 'dv_export', parts[i]),
    data: buildDependentVarExportRows(model.stages)
  }));
};
